import React, { useCallback, useEffect, useRef, useState } from "react";
import { hasDecision, onConsentChange } from "../../utils/consent";
import ConsentBanner from "./ConsentBanner";
import ConsentPreferences from "./ConsentPreferences";
// Load-bearing. `.consent-reopen` had no rule anywhere in the repo, so the
// reopener rendered as a bare, statically-positioned browser button on top of
// the page chrome on every route. Dropping this import brings that back.
import "./ConsentManager.css";

/**
 * ConsentManager — the single mounted owner of the consent UI.
 *
 * WHY THIS EXISTS: utils/consent.js fails closed, and utils/tagGate.js will not
 * load GTM (GTM-56MR8T9P, which is where GA4 G-2ZKCF74FCK lives) until a user
 * grants analytics or marketing. Without a mounted banner there is no way to
 * grant anything, so shipping the gate without this component turns every metric
 * on wtfgos.com to zero with no error anywhere. This component is what makes the
 * gate deployable.
 *
 * It is mounted ONCE in App.js as a sibling of <Routes>, deliberately outside
 * every auth wrapper: paid ad traffic lands on the public marketing site and on
 * gym subdomain landing pages, i.e. precisely the routes an authenticated-only
 * mount would skip — and those are the routes whose tags need consent most.
 */

/**
 * Public reopen mechanism (documented contract for footer "Cookie settings"
 * links and any other surface that must let a user change their mind):
 *
 *   1) import { openConsentPreferences } from ".../consent/ConsentManager";
 *      openConsentPreferences();
 *
 *   2) or, with no import at all (works from plain HTML / another bundle):
 *      window.dispatchEvent(new Event("wtfgo:open-consent-preferences"));
 *
 * Both do the same thing. The event form exists because withdrawal must stay
 * reachable from surfaces that do not import React code; a consent UI that can
 * only be opened once is a right-to-withdraw failure, not a UX nit.
 */
export const OPEN_PREFERENCES_EVENT = "wtfgo:open-consent-preferences";

export function openConsentPreferences() {
  if (typeof window === "undefined" || !window.dispatchEvent) return;
  try {
    window.dispatchEvent(new Event(OPEN_PREFERENCES_EVENT));
  } catch (e) {
    /* Never let a footer link take down the page it lives on. */
  }
}

/**
 * hasDecision() reads storage, and storage access itself throws in Safari
 * private mode and locked-down enterprise profiles. An uncaught throw here would
 * unmount the whole app (this renders above <Routes>), so we swallow and return
 * false: "we cannot prove a decision exists" must resolve to asking again, never
 * to assuming consent.
 */
function safeHasDecision() {
  try {
    return hasDecision() === true;
  } catch (e) {
    return false;
  }
}

export default function ConsentManager() {
  const [decided, setDecided] = useState(safeHasDecision);
  const [prefsOpen, setPrefsOpen] = useState(false);

  const refresh = useCallback(() => {
    setDecided(safeHasDecision());
  }, []);

  // Re-read on every consent change — including changes made in ANOTHER TAB,
  // which consent.js forwards through the same subscription. Without this the
  // banner would linger after a decision (or stay hidden after a version bump).
  useEffect(() => {
    const unsubscribe = onConsentChange(refresh);
    // Storage may have been written between the initial useState and this
    // effect (e.g. a race with another tab); re-sync once on mount.
    refresh();
    return unsubscribe;
  }, [refresh]);

  const openPrefs = useCallback(() => setPrefsOpen(true), []);
  const closePrefs = useCallback(() => {
    setPrefsOpen(false);
    // Closing preferences may have persisted a decision; re-check so the banner
    // does not reappear on top of a choice the user just made.
    refresh();
  }, [refresh]);

  // The window-event door. Cleaned up on unmount so a remount cannot end up with
  // two listeners opening the dialog twice.
  useEffect(() => {
    if (typeof window === "undefined" || !window.addEventListener) return undefined;
    window.addEventListener(OPEN_PREFERENCES_EVENT, openPrefs);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, openPrefs);
  }, [openPrefs]);

  // Survives the banner's unmount — see the comment on the button below.
  const reopenerRef = useRef(null);

  return (
    <>
      {!decided && <ConsentBanner onManage={openPrefs} />}

      {/* PERMANENT reopener. Two reasons it exists, both load-bearing:
          1. Spec §7 requires withdrawal to be as easy as granting. A banner that
             disappears after one click leaves a user who changes their mind with
             no route back — a decision they cannot revise is not really consent.
          2. Focus. Saving from the BANNER records a decision, so the banner
             unmounts and takes its "Manage" button with it. Restoring focus to
             that detached node drops the keyboard user onto <body>, losing their
             place at the exact moment the UI changes under them. This control
             outlives the banner, so it is a real element focus can return to.

          It is a fixed pill in the bottom-right corner. While the banner is
          still up it is pushed out of SIGHT (`--behind`) but deliberately not
          out of the FOCUS ORDER: the banner covers the same corner and already
          offers "Manage preferences", yet a user can close the dialog without
          deciding, and focus then lands back here. The modifier is driven from
          `decided` rather than from CSS `:has(.consent-banner)` so the rule is
          deterministic and can be asserted in a test. */}
      <button
        type="button"
        ref={reopenerRef}
        className={`consent-reopen${decided ? "" : " consent-reopen--behind"}`}
        onClick={openPrefs}
      >
        Cookie preferences
      </button>

      <ConsentPreferences
        open={prefsOpen}
        onClose={closePrefs}
        returnFocusTo={reopenerRef}
      />
    </>
  );
}
